// Сервис для работы с Firebase Cloud Messaging
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { firebaseConfig, isFirebaseConfigured, vapidKey as firebaseVapidKey } from '../firebase-config';
import api from './api';

class FCMService {
  private messaging: any = null;
  private swRegistration: ServiceWorkerRegistration | undefined;
  private isInitialized = false;
  /** Resolves when initialization finished (successfully or not). Callers
   *  must await it before isSupported(): the old code checked support at
   *  module load, before this async init, and always saw «not supported». */
  readonly ready: Promise<void>;

  constructor() {
    this.ready = this.initializeFirebase().catch(error => {
      console.error('Failed to initialize Firebase:', error);
    });
  }

  private async initializeFirebase() {
    try {
      // Проверяем конфигурацию Firebase
      if (!isFirebaseConfigured()) {
        console.warn('Firebase configuration incomplete, FCM will not work');
        return;
      }

      // Проверяем Service Worker
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker not supported, FCM will not work');
        return;
      }

      // Wait for the SW registered by serviceWorkerRegistration.ts — DO NOT
      // call register() here again (race conditions, duplicate logs).
      this.swRegistration = await navigator.serviceWorker.ready;

      const app = initializeApp(firebaseConfig);
      this.messaging = getMessaging(app);
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing Firebase FCM:', error);
    }
  }

  // Проверка поддержки FCM
  isSupported(): boolean {
    return this.isInitialized && 
           this.messaging !== null && 
           'serviceWorker' in navigator &&
           typeof window !== 'undefined';
  }

  // Получение FCM токена
  async getToken(): Promise<string | null> {
    if (!this.isSupported()) {
      console.warn('FCM not supported');
      return null;
    }

    // FCM needs the Firebase project's Web Push certificate key — NOT the
    // server's web-push VAPID key (/api/notifications/vapid-public-key is
    // empty on prod, which silently killed every token request).
    const vapidKey = (firebaseVapidKey || '').trim();
    if (!vapidKey) {
      console.warn('REACT_APP_FIREBASE_VAPID_KEY is missing — FCM will not work');
      return null;
    }

    try {
      const token = await getToken(this.messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: this.swRegistration,
      });

      if (token) {
        console.log('FCM token obtained:', token);
        // Сохраняем токен в localStorage
        localStorage.setItem('fcmToken', token);
        return token;
      } else {
        console.log('No registration token available');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  // Удаление FCM токена
  async deleteToken(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      await deleteToken(this.messaging);
      localStorage.removeItem('fcmToken');
      console.log('FCM token deleted');
      return true;
    } catch (error) {
      console.error('Error deleting FCM token:', error);
      return false;
    }
  }

  // Подписка на входящие сообщения (когда приложение активно)
  onMessage(callback: (payload: any) => void): (() => void) | null {
    if (!this.isSupported()) {
      return null;
    }

    try {
      const unsubscribe = onMessage(this.messaging, callback);
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up FCM message listener:', error);
      return null;
    }
  }

  // Отправка токена на сервер
  async sendTokenToServer(token: string): Promise<boolean> {
    try {
      // Through the shared axios client (auth + token refresh) and WITH the
      // trailing slash — POST to /api/fcm-tokens got a 301 from Django.
      const response = await api.post('/api/fcm-tokens/', {
        fcmToken: token,
        deviceInfo: this.getDeviceInfo(),
      });

      if (response.status >= 200 && response.status < 300) {
        console.log('FCM token sent to server successfully');
        return true;
      } else {
        console.error('Failed to send FCM token to server');
        return false;
      }
    } catch (error) {
      console.error('Error sending FCM token to server:', error);
      return false;
    }
  }

  // Удаление токена с сервера
  async removeTokenFromServer(token: string): Promise<boolean> {
    try {
      const response = await api.delete('/api/fcm-tokens/', { data: { fcmToken: token } });

      if (response.status >= 200 && response.status < 300) {
        console.log('FCM token removed from server successfully');
        return true;
      } else {
        console.error('Failed to remove FCM token from server');
        return false;
      }
    } catch (error) {
      console.error('Error removing FCM token from server:', error);
      return false;
    }
  }

  // Получение ID пользователя из токена
  private getUserId(): string | null {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.id;
      }
      return null;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  // Получение информации об устройстве
  private getDeviceInfo(): any {
    try {
      const userAgent = navigator?.userAgent || '';
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      
      let platform = 'unknown';
      if (isIOS) platform = 'iOS';
      else if (isAndroid) platform = 'Android';
      else if (isMobile) platform = 'Mobile';
      else platform = 'Desktop';

      return {
        userAgent: userAgent,
        platform: platform,
        isMobile: isMobile,
        isIOS: isIOS,
        isAndroid: isAndroid
      };
    } catch (error) {
      console.warn('Error getting device info:', error);
      return {
        userAgent: 'unknown',
        platform: 'unknown',
        isMobile: false,
        isIOS: false,
        isAndroid: false
      };
    }
  }

  // Получение сохраненного FCM токена
  getStoredToken(): string | null {
    return localStorage.getItem('fcmToken');
  }

  // Проверка, есть ли сохраненный токен
  hasStoredToken(): boolean {
    return !!this.getStoredToken();
  }
}

export const fcmService = new FCMService();
export default fcmService;
